import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { EntitySelect } from '@/components/EntitySelect';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormSection } from '@/components/ui/form-section';
import { InfoHint } from '@/components/ui/info-hint';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import type { ApiError } from '@/core/api';
import { resolveCompanyLabel, searchCompanies } from '@/features/crm/companies/select';
import { resolveDealLabel, searchDeals } from '@/features/crm/deals/select';
import { CURRENCIES } from '@/features/crm/deals/constants';
import { INVOICE_DIRECTIONS, INVOICE_FORM_STATUSES } from '../constants';
import { useNextInvoiceNumber } from '../queries';
import type { InvoiceInput, InvoiceProject } from '../types';
import { projectsOfAnotherClient } from '../projectOptions';
import { InvoiceProjectsField } from './InvoiceProjectsField';

const schema = z.object({
  direction: z.enum(['receivable', 'payable']),
  // Optional: a blank receivable number is auto-assigned from the org series.
  number: z.string().max(50),
  company: z.string(),
  deal: z.string(),
  projects: z.array(z.string()),
  counterparty_name: z.string().max(255),
  status: z.enum(['draft', 'issued', 'paid', 'void']),
  currency: z.enum(['USD', 'EUR', 'GBP', 'AUD']),
  issue_date: z.string(),
  due_date: z.string(),
  period_start: z.string(),
  period_end: z.string(),
  notes: z.string(),
});

type FormValues = z.infer<typeof schema>;

const FIELD_KEYS = [
  'direction', 'number', 'company', 'deal', 'projects', 'counterparty_name',
  'status', 'currency', 'issue_date', 'due_date', 'period_start', 'period_end', 'notes',
] as const;

interface InvoiceFormProps {
  defaultValues?: Partial<FormValues>;
  /** The invoice's linked projects as saved, so their names show (edit). */
  currentProjects?: InvoiceProject[];
  submitLabel: string;
  onSubmit: (values: InvoiceInput) => Promise<unknown>;
  onCancel: () => void;
}

export function InvoiceForm({
  defaultValues,
  currentProjects = [],
  submitLabel,
  onSubmit,
  onCancel,
}: InvoiceFormProps) {
  const qc = useQueryClient();
  const [droppedProjects, setDroppedProjects] = useState(0);
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      direction: 'receivable',
      number: '',
      company: '',
      deal: '',
      projects: [],
      counterparty_name: '',
      status: 'draft',
      currency: 'USD',
      issue_date: '',
      due_date: '',
      period_start: '',
      period_end: '',
      notes: '',
      ...defaultValues,
    },
  });

  async function handleSubmit(values: FormValues) {
    const input: InvoiceInput = {
      direction: values.direction,
      number: values.number,
      company: values.company ? Number(values.company) : null,
      // Only AR invoices carry a deal link (drives commission realization).
      deal: values.direction === 'receivable' && values.deal ? Number(values.deal) : null,
      // Project links → attach per-employee worklog pages to the PDF.
      projects: values.direction === 'receivable' ? values.projects.map(Number) : [],
      counterparty_name: values.counterparty_name,
      status: values.status,
      currency: values.currency,
      issue_date: values.issue_date || null,
      due_date: values.due_date || null,
      period_start: values.period_start || null,
      period_end: values.period_end || null,
      notes: values.notes,
    };
    try {
      await onSubmit(input);
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          const key = FIELD_KEYS.find((k) => k === field);
          form.setError(key ?? 'root', { message: messages.join(' ') });
        }
      }
    }
  }

  // A new client may not fit the linked projects (each belongs to the invoice's
  // company or to none): drop the ones that don't, and say so.
  function changeCompany(next: string, setCompany: (v: string) => void) {
    const linked = form.getValues('projects');
    const drop = projectsOfAnotherClient(qc, {
      from: form.getValues('company'),
      to: next,
      linked,
      current: currentProjects,
    });
    setCompany(next);
    setDroppedProjects(drop.length);
    if (drop.length > 0) {
      form.setValue(
        'projects',
        linked.filter((id) => !drop.includes(id)),
        { shouldDirty: true },
      );
    }
  }

  const submitting = form.formState.isSubmitting;
  const company = useWatch({ control: form.control, name: 'company' });
  const direction = useWatch({ control: form.control, name: 'direction' });
  const isReceivable = direction === 'receivable';

  // Prefill the number from the org's client series (<prefix>-<year>-NNNN) for a
  // new receivable invoice. Never on edit, never clobbers a typed value; a
  // switch to payable drops our suggestion so the vendor's number is entered.
  const isEdit = Boolean(defaultValues?.number);
  const { data: suggestedNumber } = useNextInvoiceNumber(isReceivable && !isEdit);
  const autoFilledNumber = useRef('');
  useEffect(() => {
    if (isEdit) return;
    const current = form.getValues('number');
    if (isReceivable) {
      if (suggestedNumber && (!current.trim() || current === autoFilledNumber.current)) {
        form.setValue('number', suggestedNumber, { shouldValidate: true });
        autoFilledNumber.current = suggestedNumber;
      }
    } else if (current && current === autoFilledNumber.current) {
      form.setValue('number', '', { shouldValidate: true });
      autoFilledNumber.current = '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedNumber, isReceivable]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="max-w-2xl space-y-5">
        <FormSection
          variant="card"
          title="Invoice"
          description="Direction and reference number."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="direction"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Direction</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {INVOICE_DIRECTIONS.map((d) => (
                        <SelectItem key={d.value} value={d.value}>
                          {d.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="number"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Number</FormLabel>
                  <FormControl>
                    <Input placeholder="INV-2026-001" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </FormSection>

        <FormSection
          variant="card"
          title="Party"
          description="Who is being billed."
        >
          <FormField
            control={form.control}
            name="company"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Company (client)</FormLabel>
                <FormControl>
                  <EntitySelect
                    queryKey="company-picker"
                    value={field.value}
                    onChange={(next) => changeCompany(next, field.onChange)}
                    search={searchCompanies}
                    resolveLabel={resolveCompanyLabel}
                    placeholder="No company"
                    clearable
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {isReceivable ? (
            <FormField
              control={form.control}
              name="deal"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1.5">
                    Deal
                    <InfoHint text="Link the deal this invoice bills — paying it realizes the sales commission." />
                  </FormLabel>
                  <FormControl>
                    <EntitySelect
                      queryKey="invoice-deal-picker"
                      value={field.value}
                      onChange={field.onChange}
                      search={searchDeals}
                      resolveLabel={resolveDealLabel}
                      placeholder="No deal"
                      clearable
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : null}

          {isReceivable ? (
            <FormField
              control={form.control}
              name="projects"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1.5">
                    Projects
                    <InfoHint text="The projects this invoice bills — this client's or ones with no client. The generated PDF attaches per-employee timesheets of their billable worklogs over the invoice period, one page per project and person." />
                  </FormLabel>
                  <FormControl>
                    <InvoiceProjectsField
                      company={company}
                      value={field.value}
                      onChange={(next) => {
                        setDroppedProjects(0);
                        field.onChange(next);
                      }}
                      current={currentProjects}
                    />
                  </FormControl>
                  {droppedProjects > 0 ? (
                    <p role="status" className="text-muted-foreground text-sm">
                      {droppedProjects === 1
                        ? 'Removed 1 project of another client.'
                        : `Removed ${droppedProjects} projects of another client.`}
                    </p>
                  ) : null}
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : null}

          <FormField
            control={form.control}
            name="counterparty_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-1.5">
                  Counterparty name
                  <InfoHint text="Used when there is no linked company (e.g. payables)." />
                </FormLabel>
                <FormControl>
                  <Input placeholder="Vendor / contractor (for AP)" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        <FormSection
          variant="card"
          title="Billing"
          description="Status, currency and key dates."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1.5">
                    Status
                    <InfoHint text="Issue or mark an invoice paid from its page — that freezes the PDF, posts it to the ledger and pays any commission." />
                  </FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {INVOICE_FORM_STATUSES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="currency"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Currency</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {CURRENCIES.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="issue_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Issue date</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="due_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Due date</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="period_start"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1.5">
                    Period start
                    <InfoHint text="Service period being billed. Printed on the invoice and used as the window for attached worklogs." />
                  </FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="period_end"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Period end</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </FormSection>

        <FormSection variant="card" title="Notes" description="Anything else worth recording.">
          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Notes</FormLabel>
                <FormControl>
                  <Textarea rows={3} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        {form.formState.errors.root ? (
          <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
        ) : null}

        <div className="flex gap-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : submitLabel}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Form>
  );
}
